(() => {
  const selector = '[data-sv-setup-auto-cart]';
  const collectionLinks = {
    leash: {label: 'See all leashes', url: '/collections/leashes'},
    traction: {label: 'See all traction', url: '/collections/traction'},
    fins: {label: 'See all fins', url: '/collections/fins'},
    boardbag: {label: 'See all covers', url: '/collections/boardbags'},
    coil: {label: 'See all bodyboard leashes', url: '/collections/bodyboard-leashes'},
    'bodyboard-bag': {label: 'See all bodyboard bags', url: '/collections/bodyboard-accessories'},
    car: {label: 'See all car accessories', url: '/collections/surf-car-accessories'},
    wax: {label: 'See all wax', url: '/collections/wax'}
  };

  const groupFromInput = (input) => {
    if (!input) return '';
    return Object.keys(collectionLinks).find((group) => input.name.endsWith(`-${group}`)) || '';
  };

  // 24 Oct 2026: colour-family ordering (Chris's rule, every colour). The board's own colour leads in
  // every leash, traction and cover row of every builder: pink boards get pink, then white, pink/white,
  // lavender; black & white boards get black, black/white, white, grey; yellow and rust boards get
  // yellow, rust, orange; blue boards blue, berry, sky; tan/beige boards tan, mint, berry ... Each board's
  // palette is its own colour, then its second colour, then the rest of that colour story. Black, white
  // and grey count as neutrals (no penalty) where they are not part of the story. Size ranking still
  // wins (the leash lengths the section scripts rank for the chosen size); this orders the cards inside
  // each size rank and writes the result to CSS order, which is what the flex/grid rows paint. Cards
  // carry data-sv-colour (title colour + variant colour, lower case) from snippets/sv-setup-option.
  const COLOUR_FAMILIES = {
    pink: ['pink', 'blush', 'white', 'lilac', 'lavender', 'purple', 'berry'],
    lilac: ['lilac', 'lavender', 'purple', 'pink', 'blush', 'white'],
    black: ['black', 'white', 'grey', 'silver'],
    white: ['white', 'black', 'grey', 'natural', 'wood', 'silver'],
    blue: ['blue', 'sky', 'navy', 'berry', 'white'],
    yellow: ['yellow', 'gold', 'rust', 'orange', 'red'],
    rust: ['rust', 'brown', 'yellow', 'orange', 'red', 'tan'],
    orange: ['orange', 'yellow', 'rust', 'gold', 'red'],
    red: ['red', 'rust', 'orange'],
    earth: ['tan', 'beige', 'sand', 'natural', 'wood', 'mint', 'sage', 'berry', 'brown', 'rust'],
    green: ['green', 'sage', 'mint', 'olive', 'tan']
  };
  const FAMILY_OF = {
    pink: 'pink', blush: 'pink', berry: 'pink',
    lilac: 'lilac', lavender: 'lilac', purple: 'lilac',
    black: 'black', grey: 'black', silver: 'black',
    white: 'white',
    blue: 'blue', sky: 'blue', navy: 'blue',
    yellow: 'yellow', gold: 'yellow',
    rust: 'rust', brown: 'rust',
    orange: 'orange',
    red: 'red',
    tan: 'earth', beige: 'earth', sand: 'earth', natural: 'earth', wood: 'earth',
    green: 'green', sage: 'green', mint: 'green', olive: 'green'
  };
  // Option codes and shade names used on products, mapped to the colour words above.
  const COLOUR_ALIASES = {
    blk: 'black', blu: 'blue', grn: 'green', pnk: 'pink', gry: 'grey', gray: 'grey', wht: 'white', org: 'orange', yel: 'yellow', lav: 'lavender',
    rose: 'pink', coral: 'pink', merlot: 'red', burgundy: 'red', maroon: 'red', wine: 'red', charcoal: 'grey', cream: 'white', ivory: 'white',
    teal: 'blue', aqua: 'blue', turquoise: 'blue', ocean: 'blue', lime: 'green', khaki: 'tan', camel: 'tan', chocolate: 'brown'
  };
  const NEUTRAL_COLOURS = ['black', 'white', 'grey', 'silver'];
  const FAMILY_SCOPES = ['leash', 'traction', 'boardbag'];

  // Colour words in the order they are written, de-duplicated; anything that is not a colour is ignored.
  const colourWords = (text) => {
    const words = [];
    (text || '').toLowerCase().split(/[^a-z]+/).forEach((raw) => {
      const word = COLOUR_ALIASES[raw] || raw;
      if (FAMILY_OF[word] && !words.includes(word)) words.push(word);
    });
    return words;
  };

  // The board's colourway, in three parts: the named colourway (the section can declare it; otherwise
  // the full product title - og:title, as the page heading drops the colour on surfboard pages - after
  // its dash), the selected variant's options (softboards carry the colour as a Colour option) and the
  // board's colour tags.
  const selectedVariantId = (section) => section.dataset.svSetupSelectedVariant || section.dataset.svRenderedVariant || '';
  const boardColourText = (section) => {
    const parts = {title: section.dataset.svBoardColourway || '', variant: '', tags: (section.dataset.svBoardColours || '').replace(/\|/g, ' ')};
    if (!parts.title) {
      const productId = (section.dataset.svSetupOwner || '').split('-').pop();
      const product = /^\d+$/.test(productId)
        ? document.querySelector(`.product-section[data-product-id="${productId}"]`)
        : null;
      const meta = document.querySelector('meta[property="og:title"]');
      const heading = (product || document).querySelector('.product-single__title, h1');
      const title = (meta && meta.getAttribute('content')) || (heading && heading.textContent) || '';
      const dash = Math.max(title.lastIndexOf('–'), title.lastIndexOf('—'), title.lastIndexOf(' - '));
      if (dash !== -1) parts.title = title.slice(dash + 1);
      const selectedId = selectedVariantId(section);
      const json = product && product.querySelector('[data-variant-json]');
      if (json && selectedId) {
        try {
          const variants = JSON.parse(json.value || json.textContent);
          const variant = Array.isArray(variants) && variants.find((item) => String(item.id) === String(selectedId));
          if (variant) parts.variant = [...(variant.options || []), variant.title || ''].join(' ');
        } catch (error) {
          // no variant data - the title is enough
        }
      }
    }
    return parts;
  };

  const boardFamily = (section) => {
    const variantId = selectedVariantId(section);
    if (section.svBoardFamily !== undefined && section.svBoardFamilyVariant === variantId) return section.svBoardFamily;
    section.svBoardFamilyVariant = variantId;
    const parts = boardColourText(section);
    const words = colourWords(parts.title);
    // The named colourway leads. The variant option (a softboard's Colour option) and the colour tags add
    // colours the name leaves out, but not a neutral: black rails on a "Red" board don't make black a
    // board colour - black stays a neutral there, and red/rust hardware leads.
    [parts.variant, parts.tags].forEach((source) => {
      colourWords(source).forEach((word) => {
        if (words.includes(word)) return;
        if (words.length && !NEUTRAL_COLOURS.includes(words[0]) && NEUTRAL_COLOURS.includes(word)) return;
        words.push(word);
      });
    });
    const dominant = words[0];
    if (!dominant) {
      section.svBoardFamily = null;
      return null;
    }
    // A board with a neutral base and a colour accent (White/Blue, White/Wood) also takes the accent's
    // story, after its own: white, blue, black, grey ... then sky, navy, berry.
    const accent = NEUTRAL_COLOURS.includes(dominant) ? words.find((word) => !NEUTRAL_COLOURS.includes(word)) : null;
    const palette = [];
    [...words, ...COLOUR_FAMILIES[FAMILY_OF[dominant]], ...(accent ? COLOUR_FAMILIES[FAMILY_OF[accent]] : [])].forEach((word) => {
      if (!palette.includes(word)) palette.push(word);
    });
    section.svBoardFamily = {
      name: FAMILY_OF[dominant],
      palette,
      stories: (section.dataset.svBoardStories || '').split('|').filter(Boolean)
    };
    section.dataset.svBoardFamily = section.svBoardFamily.name;
    return section.svBoardFamily;
  };

  // Sort key, lowest first: [has a colour outside the palette (forgiven when the card shares the board's
  // colour story), the board-palette position of the card's main colour (a white/black/grey base counts
  // as the main colour, e.g. White/Pink sits with white), carries a neutral the story doesn't use, is a
  // two-colour combo, position of its second colour, no shared colour story]. Cards with none of the
  // palette's colours go last, story matches before the rest.
  const rankCard = (card, family) => {
    const words = colourWords(card.dataset.svColour);
    const inFamily = words.filter((word) => family.palette.includes(word));
    const neutral = words.filter((word) => !family.palette.includes(word) && NEUTRAL_COLOURS.includes(word));
    const foreign = words.filter((word) => !family.palette.includes(word) && !NEUTRAL_COLOURS.includes(word));
    const tags = (card.dataset.svOptionTags || card.dataset.svLeashTags || '').split('|');
    const story = family.stories.some((name) => tags.includes(name) || tags.includes(`${name}-length`) || tags.includes(`${name}-restock`)) ? 0 : 1;
    // front pads never lead a traction row (Chris's merchandising order puts them last)
    if (tags.includes('front-pad')) return [4, 0, 0, 0, 0, story];
    if (!inFamily.length) return [story === 0 ? 2 : 3, 0, 0, 0, 0, story];
    const positions = inFamily.map((word) => family.palette.indexOf(word));
    const dominant = NEUTRAL_COLOURS.includes(inFamily[0]) ? positions[0] : Math.min(...positions);
    const others = positions.slice();
    others.splice(positions.indexOf(dominant), 1);
    const accent = others.length ? Math.min(...others) : 99;
    // a colour outside the palette drops the card behind the pure ones, unless the card is tagged into
    // the board's own colour story (Chris's call on what belongs together)
    const outside = foreign.length && story ? 1 : 0;
    return [outside, dominant, neutral.length ? 1 : 0, inFamily.length > 1 ? 1 : 0, accent, story];
  };
  const compareKeys = (a, b) => {
    for (let index = 0; index < a.length; index += 1) {
      if (a[index] !== b[index]) return a[index] - b[index];
    }
    return 0;
  };

  const isShownCard = (card) => {
    const input = card.querySelector('[data-sv-setup-item]');
    return Boolean(input) && !input.disabled && !card.hidden && !card.classList.contains('sv-rule-hidden');
  };

  // Size rank first: extra leash cards carry data-sv-leash-rank from the section script (best length = 1);
  // curated leash cards are ranked by their length tag the same way, so a black curated pick can drop
  // behind a colour-matched leash of the same length. Other rows have no size rank (hidden cards last).
  const cardOrder = (card, leashRank) => {
    if (card.hidden) return 99;
    const rank = parseInt(card.dataset.svLeashRank || '0', 10) || 0;
    if (rank) return rank;
    if (!leashRank.length) return 0;
    // no rank from the section script yet (no size selected): read the length off the card itself
    const sizeMatch = card.dataset.svSizeMatch || '';
    const tagLength = (card.dataset.svLeashTags || '').split('|').find((tag) => /^\d+ft$/.test(tag));
    const length = sizeMatch.startsWith('len-') ? sizeMatch.slice(4) : (tagLength ? tagLength.replace('ft', '') : '');
    if (!length) return 0;
    const position = leashRank.indexOf(length);
    return position === -1 ? leashRank.length + 1 : position + 1;
  };

  const orderByFamily = (section) => {
    const family = boardFamily(section);
    if (!family) return false;
    const board = section.querySelector('[data-sv-setup-board]:checked');
    const boardRank = ((board && board.dataset.svBoardLeashRank) || '').split(',').filter(Boolean);
    section.querySelectorAll('.sv-setup-options, .sv-category-setup__options').forEach((row) => {
      const cards = [...row.children].filter((card) => card.classList.contains('sv-setup-option'));
      if (!cards.length) return;
      if (!FAMILY_SCOPES.includes(groupFromInput(cards[0].querySelector('[data-sv-setup-item]')))) return;
      // leash lengths best-first: from the selected size, else the row's own rank (no size selectable)
      const leashRank = boardRank.length ? boardRank : (row.dataset.svLeashRank || '').split(',').filter(Boolean);
      const hadTopMatch = cards.some((card) => card.querySelector('.sv-setup-option__priority'));
      cards.forEach((card, index) => {
        // the row's original (Liquid) order, remembered the first time through: curated primary, backup, extras
        if (!card.dataset.svRowIndex) card.dataset.svRowIndex = String(index + 1);
        // undo the single-card promotion the surfboard section script may have applied first
        if (card.dataset.svColourMatch !== undefined || card.dataset.svBestFirst !== undefined) {
          delete card.dataset.svColourMatch;
          delete card.dataset.svBestFirst;
          card.style.order = card.dataset.svLeashRank || '';
        }
        card.querySelectorAll('[data-sv-colour-badge], [data-sv-family-badge]').forEach((node) => node.remove());
      });
      const rowIndex = (card) => parseInt(card.dataset.svRowIndex, 10) || 0;
      // Chris's curated pick (the card marked recommended, else the first curated card showing for this
      // size - the Liquid renders primary first) stays in front while it is in the board's colour story
      // and no other card in the row matches the board's main colour better: it leads with the board's
      // own colour, carries the board's story tag, or nothing shown beats its colour. A generic black
      // default on a coloured board drops behind the colour matches.
      const shownCurated = cards
        .filter((card) => !card.hasAttribute('data-sv-expanded-option') && isShownCard(card))
        .sort((a, b) => rowIndex(a) - rowIndex(b));
      const curated = shownCurated.find((card) => card.classList.contains('sv-setup-option--recommended')) || shownCurated[0];
      const keys = new Map(cards.map((card) => [card, rankCard(card, family)]));
      const bestDominant = Math.min(...cards
        .filter((card) => isShownCard(card) && keys.get(card)[0] === 0)
        .map((card) => keys.get(card)[1]), 99);
      const ranked = cards.map((card) => {
        const key = keys.get(card);
        const inStory = key[0] === 0 && (key[1] === 0 || key[5] === 0 || key[1] <= bestDominant);
        return {
          card,
          index: rowIndex(card),
          order: cardOrder(card, leashRank),
          pinned: card === curated && inStory ? 0 : 1,
          key
        };
      });
      ranked.sort((a, b) => a.order - b.order || a.pinned - b.pinned || compareKeys(a.key, b.key) || a.index - b.index);
      // The rows are flex/grid, so CSS order decides what shows first: write the final position to every
      // card (curated cards otherwise sit at order 0, ahead of the size-ranked extras whatever the DOM says).
      ranked.forEach(({card}, position) => {
        row.appendChild(card);
        card.style.order = String(position + 1);
      });
      const first = ranked.find((entry) => isShownCard(entry.card));
      if (!first) return;
      cards.forEach((card) => {
        if (card === first.card) return;
        card.querySelectorAll('.sv-setup-option__priority').forEach((node) => node.remove());
      });
      const copy = first.card.querySelector('.sv-setup-option__copy');
      if (!copy) return;
      if (first.key[0] <= 1 && !copy.querySelector('.sv-setup-option__badge')) {
        const badge = document.createElement('span');
        badge.className = 'sv-setup-option__badge sv-setup-option__badge--story';
        badge.dataset.svFamilyBadge = '';
        badge.textContent = 'Colour match';
        copy.prepend(badge);
      }
      if (hadTopMatch && !copy.querySelector('.sv-setup-option__priority')) {
        const label = document.createElement('span');
        label.className = 'sv-setup-option__priority';
        label.textContent = 'Top match';
        copy.prepend(label);
      }
      row.scrollTo({left: 0});
    });
    return true;
  };
  window.svSetupFamilyOrder = orderByFamily;

  const updateQuickAdd = (section) => {
    section.querySelectorAll('[data-sv-setup-toggle]').forEach((button) => {
      const input = section.querySelector(`#${CSS.escape(button.dataset.svSetupToggle)}`);
      const selected = Boolean(input && input.checked);
      button.textContent = selected ? 'Added ✓' : 'Add';
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.disabled = !input || input.disabled || section.dataset.svSetupBoardAvailable === 'false';
    });
  };

  const initialiseScroller = (row) => {
    if (row.dataset.svScrollerReady === 'true') return;

    // One card per variant: the curated cards win wherever they sit in the row (the colour-family ordering
    // may already have moved hidden curated cards behind the extras), duplicate extras are dropped.
    const seenVariants = new Set();
    const rowCards = [...row.querySelectorAll('.sv-setup-option')];
    rowCards.filter((card) => !card.hasAttribute('data-sv-expanded-option')).forEach((card) => {
      const cardInput = card.querySelector('[data-sv-setup-item]');
      if (cardInput) seenVariants.add(cardInput.value);
    });
    rowCards.filter((card) => card.hasAttribute('data-sv-expanded-option')).forEach((card) => {
      const cardInput = card.querySelector('[data-sv-setup-item]');
      if (!cardInput) return;
      if (seenVariants.has(cardInput.value)) {
        card.remove();
        return;
      }
      seenVariants.add(cardInput.value);
    });

    const input = row.querySelector('[data-sv-setup-item]');
    const group = groupFromInput(input);
    const collection = collectionLinks[group];
    if (!collection) return;

    if (group === 'coil') {
      const sellingPoints = [
        ['Quick wrist fit', 'Easy on and off between waves and beach sessions'],
        ['Compact coil', 'Stays tidy and out of the way while you paddle and ride'],
        ['Useful spare', 'A practical backup for family beach days and travel']
      ];
      row.querySelectorAll('.sv-setup-option[data-sv-expanded-option]').forEach((card, index) => {
        const copy = card.querySelector('.sv-setup-option__copy');
        const title = copy && copy.querySelector(':scope > strong');
        if (!copy || !title) return;

        let badge = copy.querySelector('.sv-setup-option__badge');
        let detail = copy.querySelector(':scope > small');
        const point = sellingPoints[index];
        if (!point) {
          if (badge) badge.remove();
          if (detail) detail.remove();
          return;
        }

        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'sv-setup-option__badge';
          title.insertAdjacentElement('beforebegin', badge);
        }
        if (!detail) {
          detail = document.createElement('small');
          title.insertAdjacentElement('afterend', detail);
        }
        badge.textContent = point[0];
        detail.textContent = point[1];
      });
    }

    row.dataset.svScrollerReady = 'true';
    row.classList.add('sv-setup-options--scroll');
    row.setAttribute('tabindex', '0');
    row.setAttribute('aria-label', `${collection.label.replace('See all ', '')}; best matches first`);

    const controls = document.createElement('div');
    controls.className = 'sv-setup-scroll-controls';
    controls.innerHTML = `
      <span class="sv-setup-scroll-controls__hint">Best matches first <span aria-hidden="true">·</span> Keep scrolling for more</span>
      <span class="sv-setup-scroll-controls__actions">
        <button type="button" class="sv-setup-scroll-controls__button" data-sv-scroll-prev aria-label="Previous choices">←</button>
        <button type="button" class="sv-setup-scroll-controls__button" data-sv-scroll-next aria-label="More choices">→</button>
        <a class="sv-setup-scroll-controls__all" href="${collection.url}">${collection.label}</a>
      </span>`;
    row.insertAdjacentElement('afterend', controls);

    const previous = controls.querySelector('[data-sv-scroll-prev]');
    const next = controls.querySelector('[data-sv-scroll-next]');
    const scrollAmount = () => Math.max(row.clientWidth * .82, 280);
    previous.addEventListener('click', () => row.scrollBy({left: -scrollAmount(), behavior: 'smooth'}));
    next.addEventListener('click', () => row.scrollBy({left: scrollAmount(), behavior: 'smooth'}));

    const update = () => {
      const hasOverflow = row.scrollWidth > row.clientWidth + 4;
      previous.hidden = !hasOverflow;
      next.hidden = !hasOverflow;
      previous.disabled = row.scrollLeft <= 4;
      next.disabled = row.scrollLeft >= row.scrollWidth - row.clientWidth - 4;
      controls.classList.toggle('sv-setup-scroll-controls--single', !hasOverflow);
    };
    row.addEventListener('scroll', update, {passive: true});
    window.addEventListener('resize', update, {passive: true});
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(row);
    new MutationObserver(update).observe(row, {subtree: true, attributes: true, attributeFilter: ['hidden', 'disabled']});
    window.setTimeout(update, 60);
  };

  const request = async (path, options = {}) => {
    const response = await fetch(`${window.Shopify.routes.root}${path}`, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? {'Content-Type': 'application/json'} : {}),
        ...(options.headers || {})
      }
    });
    const result = await response.json();
    if (!response.ok) throw result;
    return result;
  };

  const selectedItems = (section) => {
    return [...section.querySelectorAll('[data-sv-setup-item]:checked:not(:disabled)')]
      .map((input) => ({
        id: Number(input.value),
        group: input.dataset.svSetupBoard === undefined ? input.name : 'board',
        sellingPlan: Number(input.dataset.svSellingPlan) || null,
        preorderUnresolved: input.dataset.svPreorderUnresolved === 'true'
      }))
      .filter((item) => Number.isFinite(item.id));
  };

  const updateCartIndicators = (cart) => {
    document.querySelectorAll('[data-sv-cart-count]').forEach((badge) => {
      badge.textContent = cart.item_count;
      badge.hidden = cart.item_count === 0;
    });

    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {cart}
    }));
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('sv-setup:cart-updated', {
      detail: {cart}
    }));
  };

  const formatCartTotal = (cents) => {
    const currency = window.Shopify && window.Shopify.currency
      ? window.Shopify.currency.active
      : 'AUD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency',
        currency
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const initialiseBoardSelection = (section) => {
    const productId = (section.dataset.svSetupOwner || '').split('-').pop();
    if (!/^\d+$/.test(productId)) return;
    const product = document.querySelector(`.product-section[data-product-id="${productId}"][data-history="true"]`);
    const json = product && product.querySelector('[data-variant-json]');
    if (!json) return;

    let variants;
    try {
      variants = JSON.parse(json.value || json.textContent);
    } catch (error) {
      return;
    }
    if (!Array.isArray(variants) || !variants.length) return;

    const byId = new Map(variants.map((variant) => [String(variant.id), variant]));
    const controls = [...product.querySelectorAll('[data-variant-input]')];
    const boards = [...section.querySelectorAll('[data-sv-setup-board]')];
    const boardOptions = section.querySelector('.sv-setup-board-options, .sv-category-setup__board-options');
    if (!boards.length || !boardOptions) return;

    const selectedSummary = section.querySelector('[data-sv-selected-board-summary]');
    const changeBoard = section.querySelector('[data-sv-change-board]');
    if (changeBoard) changeBoard.href = `#${product.id}`;

    const unavailable = document.createElement('p');
    unavailable.setAttribute('data-sv-board-selection-status', '');
    unavailable.setAttribute('role', 'status');
    unavailable.setAttribute('aria-live', 'polite');
    unavailable.hidden = true;
    boardOptions.insertAdjacentElement('afterend', unavailable);
    let mirroring = false;

    const nativeSelection = () => {
      const selected = controls.filter((input) => input.type !== 'radio' || input.checked);
      if (selected.length) {
        return variants.find((variant) => selected.every((input) => (
          variant[input.dataset.index] === input.value
        ))) || null;
      }
      const master = product.querySelector('[data-product-select]');
      return byId.get(master && master.value) || null;
    };

    let refreshVersion = 0;
    const refreshRecommendations = async (chosen) => {
      if (!chosen || !section.dataset.svRefreshSection ||
          section.dataset.svRenderedVariant === String(chosen.id)) return;
      const version = ++refreshVersion;
      const selected = new Set([...section.querySelectorAll('[data-sv-setup-item]:checked')].map(input => input.value));
      section.dataset.svRenderedVariant = String(chosen.id);
      section.setAttribute('aria-busy', 'true');
      section.querySelectorAll('[data-sv-setup-item]:not([data-sv-setup-board]), [data-sv-setup-toggle]').forEach(input => { input.disabled = true; });
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', chosen.id);
        url.searchParams.set('section_id', section.dataset.svRefreshSection);
        const response = await fetch(url, {credentials: 'same-origin'});
        if (!response.ok) throw new Error('Could not load matching accessories.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = html.querySelector('[data-sv-refresh-section]');
        if (!next || next.dataset.svRenderedVariant !== String(chosen.id)) throw new Error('Could not load matching accessories.');
        if (version !== refreshVersion || !section.isConnected) return;
        next.querySelectorAll('[data-sv-setup-item]:not([data-sv-setup-board])').forEach(input => { input.checked = selected.has(input.value) && !input.disabled; });
        const previousDisclosure = section.querySelector('[data-sv-setup-disclosure]');
        const nextDisclosure = next.querySelector('[data-sv-setup-disclosure]');
        if (previousDisclosure && nextDisclosure) nextDisclosure.open = previousDisclosure.open;
        section.replaceWith(next);
        initialiseAll(next);
      } catch (error) {
        if (version !== refreshVersion || !section.isConnected) return;
        let notice = section.querySelector('[data-sv-match-error]');
        if (!notice) {
          notice = document.createElement('p');
          notice.dataset.svMatchError = '';
          notice.setAttribute('role', 'status');
          section.prepend(notice);
        }
        notice.textContent = 'Please refresh this page to load accessories for the selected size.';
      } finally {
        if (version === refreshVersion) section.removeAttribute('aria-busy');
      }
    };

    const reflect = (variant) => {
      const chosen = variant && byId.get(String(variant.id));
      let selectedBoard = null;
      boards.forEach((input) => {
        const option = byId.get(input.value);
        input.disabled = !option || !option.available;
        input.checked = Boolean(chosen && chosen.available && input.value === String(chosen.id));
        if (input.checked) selectedBoard = input;
      });
      section.dataset.svSetupSelectedVariant = chosen ? String(chosen.id) : '';
      section.dataset.svSetupBoardAvailable = selectedBoard ? 'true' : 'false';
      if (selectedSummary) {
        selectedSummary.hidden = !selectedBoard;
        // 28 Oct 2026: the summary is name / size & colour / price, styled like the product cards; fill the parts when present
        const selectedVariant = selectedSummary.querySelector('[data-sv-selected-variant]');
        const selectedPrice = selectedSummary.querySelector('[data-sv-selected-price]');
        if (selectedVariant && selectedPrice) {
          selectedVariant.textContent = chosen ? chosen.title : '';
          selectedPrice.textContent = chosen ? formatCartTotal(chosen.price) : '';
        } else {
          selectedSummary.textContent = chosen ? `${chosen.title} — ${formatCartTotal(chosen.price)}` : '';
        }
      }
      unavailable.hidden = Boolean(selectedBoard);
      if (!selectedBoard) {
        const strings = window.theme && window.theme.strings || {};
        const message = chosen && !chosen.available ? strings.soldOut : strings.unavailable;
        unavailable.textContent = [
          chosen && chosen.title,
          chosen && formatCartTotal(chosen.price),
          message || section.dataset.svBoardUnavailable
        ].filter(Boolean).join(' — ');
      }
      section.dispatchEvent(new CustomEvent('sv-setup:board-selection', {
        detail: {variant: chosen || null, board: selectedBoard}
      }));
      updateQuickAdd(section);
      refreshRecommendations(chosen);
    };

    product.addEventListener('variantChange', (event) => {
      if (!section.isConnected || !event.detail) return;
      reflect(event.detail.variant || null);
    });

    section.addEventListener('change', (event) => {
      if (!event.target.matches('[data-sv-setup-board]') || mirroring) return;
      const variant = byId.get(event.target.value);
      if (!variant || !variant.available) {
        reflect(nativeSelection());
        return;
      }
      mirroring = true;
      try {
        let changedInput = null;
        controls.forEach((input) => {
          const value = variant[input.dataset.index];
          if (value === undefined) return;
          if (input.type === 'radio') {
            const checked = input.value === value;
            if (checked && !input.checked && !changedInput) changedInput = input;
            input.checked = checked;
          } else if (input.value !== value) {
            input.value = value;
            if (!changedInput) changedInput = input;
          }
        });
        if (changedInput) changedInput.dispatchEvent(new Event('change', {bubbles: true}));
        reflect(nativeSelection());
      } finally {
        mirroring = false;
      }
    });

    reflect(nativeSelection());
  };

  // 30 Sep 2026: until this board is in the cart the bar offers "Build setup with discount"
  // (scrolls to the setup) and "Checkout – board only" (adds the chosen board, goes to checkout).
  // Once the board is in the cart it switches to the cart summary + "View cart" / "Checkout".
  const initialiseStickyCart = (section) => {
    const persistent = section.hasAttribute('data-sv-sticky-always');
    const disclosure = section.querySelector('[data-sv-setup-disclosure]');
    let sectionVisible = persistent;
    const stickyTitle = section.dataset.svSetupStickyTitle || 'Your setup cart';
    const root = window.Shopify.routes.root;
    const owner = section.dataset.svSetupOwner || '';
    const BUILD_TITLE = 'Complete your board setup';
    const BUILD_TEXT = 'with discounted hardware & accessories';
    const BOARD_ONLY = 'Checkout – board only';
    const BUILD = 'Build setup with discount';
    const bar = document.createElement('aside');
    bar.className = 'sv-setup-sticky-cart sv-setup-sticky-cart--build';
    bar.setAttribute('data-sv-setup-sticky-cart', '');
    bar.setAttribute('aria-label', 'Board setup and checkout');
    bar.hidden = !persistent || Boolean(disclosure && !disclosure.open);
    bar.innerHTML = `
      <div class="sv-setup-sticky-cart__copy">
        <strong data-sv-setup-sticky-title>${BUILD_TITLE}</strong>
        <span data-sv-setup-sticky-summary>${BUILD_TEXT.replace('&', '&amp;')}</span>
      </div>
      <div class="sv-setup-sticky-cart__actions">
        <a class="sv-setup-sticky-cart__view" data-sv-sticky-secondary href="${root}checkout">${BOARD_ONLY}</a>
        <a class="sv-setup-sticky-cart__checkout" data-sv-sticky-primary href="#complete-your-setup" aria-disabled="false">${BUILD}</a>
      </div>`;
    section.appendChild(bar);
    section.classList.add('sv-setup-sticky-cart-enabled');

    const title = bar.querySelector('[data-sv-setup-sticky-title]');
    const summary = bar.querySelector('[data-sv-setup-sticky-summary]');
    const primary = bar.querySelector('[data-sv-sticky-primary]');
    const secondary = bar.querySelector('[data-sv-sticky-secondary]');
    let inCart = false;
    let busy = false;

    const boardIds = () => new Set([...section.querySelectorAll('[data-sv-setup-board]')].map((input) => Number(input.value)));
    const update = (cart) => {
      const items = (cart && cart.items) || [];
      const count = Number(cart && cart.item_count) || 0;
      const ids = boardIds();
      inCart = items.some((item) => ids.has(item.variant_id) || (item.properties && item.properties._sv_setup_owner === owner));
      bar.classList.toggle('sv-setup-sticky-cart--build', !inCart);
      if (inCart) {
        title.textContent = stickyTitle;
        summary.textContent = `${count} ${count === 1 ? 'item' : 'items'} · ${formatCartTotal(cart.total_price)}`;
        secondary.textContent = 'View cart';
        secondary.href = `${root}cart`;
        primary.textContent = 'Checkout';
        primary.href = `${root}checkout`;
      } else {
        title.textContent = BUILD_TITLE;
        summary.textContent = BUILD_TEXT;
        secondary.textContent = BOARD_ONLY;
        secondary.href = `${root}checkout`;
        primary.textContent = BUILD;
        primary.href = '#complete-your-setup';
      }
    };

    const goToSetup = () => {
      if (disclosure) disclosure.open = true;
      // land on the first accessory step heading (e.g. "02 Leash"), not halfway into its cards
      const options = section.querySelector('.sv-category-setup__options, .sv-setup-options');
      const target = (options && options.closest('.sv-category-setup__group, .sv-setup-group')) || options || section;
      const top = target.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    };

    const boardOnly = async () => {
      const input = section.querySelector('[data-sv-setup-board]:checked:not(:disabled)');
      if (!input || section.dataset.svSetupBoardAvailable === 'false') {
        summary.textContent = 'Choose your board size first';
        goToSetup();
        return;
      }
      if (input.dataset.svPreorderUnresolved === 'true') {
        window.location.href = `${root}cart`;
        return;
      }
      busy = true;
      secondary.textContent = 'Adding board…';
      try {
        const plan = Number(input.dataset.svSellingPlan) || null;
        await request('cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ items: [{ id: Number(input.value), quantity: 1, ...(plan ? { selling_plan: plan } : {}) }] })
        });
        window.location.href = `${root}checkout`;
      } catch (error) {
        busy = false;
        secondary.textContent = BOARD_ONLY;
        summary.textContent = (error && (error.description || error.message)) || 'Could not add the board. Please try again.';
      }
    };

    primary.addEventListener('click', (event) => {
      if (inCart) return;
      event.preventDefault();
      goToSetup();
    });
    secondary.addEventListener('click', (event) => {
      if (inCart) return;
      event.preventDefault();
      if (!busy) boardOnly();
    });

    document.addEventListener('sv-setup:cart-updated', (event) => {
      if (event.detail && event.detail.cart) update(event.detail.cart);
    });
    request('cart.js').then(update).catch(() => {});

    const updateVisibility = () => {
      bar.hidden = !sectionVisible || Boolean(disclosure && !disclosure.open);
    };
    if (disclosure) disclosure.addEventListener('toggle', updateVisibility);
    if (persistent) {
      updateVisibility();
    } else if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        sectionVisible = entries.some((entry) => entry.isIntersecting);
        updateVisibility();
      }).observe(section);
    } else {
      sectionVisible = true;
      updateVisibility();
    }
  };

  const syncCart = async (section) => {
    const owner = section.dataset.svSetupOwner;
    const desired = selectedItems(section);
    if (desired.some((item) => item.preorderUnresolved)) {
      throw new Error('A preorder option needs checking. Open its product details or remove it from your setup and try again.');
    }
    const board = desired.find((item) => item.group === 'board');
    if (!owner || !board || section.dataset.svSetupBoardAvailable === 'false') {
      throw new Error('Choose your board first.');
    }

    let cart = await request('cart.js');
    const managed = cart.items.filter((item) => (
      item.properties && item.properties._sv_setup_owner === owner
    ));
    const desiredByVariant = new Map(desired.map((item) => [item.id, item]));
    const retained = new Set();
    const existingBoard = cart.items.find((item) => (
      item.variant_id === board.id && item.quantity > 0 &&
      !(item.properties && item.properties._sv_setup_owner) &&
      (Number(item.selling_plan_allocation?.selling_plan?.id) || null) === board.sellingPlan
    ));
    if (existingBoard) retained.add(board.id);
    const updates = {};

    managed.forEach((item) => {
      const desiredItem = desiredByVariant.get(item.variant_id);
      const cartPlan = Number(item.selling_plan_allocation?.selling_plan?.id) || null;
      if (!desiredItem || retained.has(item.variant_id) || cartPlan !== desiredItem.sellingPlan) {
        updates[item.key] = 0;
        return;
      }

      retained.add(item.variant_id);
      if (item.quantity !== 1) updates[item.key] = 1;
    });

    if (Object.keys(updates).length) {
      cart = await request('cart/update.js', {
        method: 'POST',
        body: JSON.stringify({updates})
      });
    }

    const itemsToAdd = desired
      .filter((item) => !retained.has(item.id))
      .map((item) => ({
        id: item.id,
        quantity: 1,
        ...(item.sellingPlan ? {selling_plan: item.sellingPlan} : {}),
        properties: {
          _sv_setup_owner: owner,
          _sv_setup_group: item.group
        }
      }));

    if (itemsToAdd.length) {
      await request('cart/add.js', {
        method: 'POST',
        body: JSON.stringify({items: itemsToAdd})
      });
    }

    cart = await request('cart.js');
    updateCartIndicators(cart);
    return {cart, itemCount: desired.length};
  };

  const initialise = (section) => {
    if (!section || section.dataset.setupAutoCartReady === 'true') return;
    section.dataset.setupAutoCartReady = 'true';

    const status = document.createElement('p');
    status.className = 'sv-setup-auto-cart-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;
    section.appendChild(status);

    const state = {syncing: false, queued: false};
    // listens after the section's own size-match handler (registered earlier), so the colour
    // ordering is applied to the re-ranked rows on every size change
    section.addEventListener('sv-setup:board-selection', () => orderByFamily(section));
    initialiseBoardSelection(section);
    let statusTimer;
    const showStatus = (message, duration = 2400) => {
      window.clearTimeout(statusTimer);
      status.textContent = message;
      status.hidden = false;
      statusTimer = window.setTimeout(() => {
        status.hidden = true;
      }, duration);
    };

    const run = async () => {
      if (state.syncing) return;
      state.syncing = true;
      section.setAttribute('aria-busy', 'true');

      try {
        while (state.queued) {
          state.queued = false;
          showStatus('Updating your cart…', 10000);
          const result = await syncCart(section);
          showStatus(`✓ Cart updated — ${result.itemCount} setup ${result.itemCount === 1 ? 'item' : 'items'} selected.`);
        }
      } catch (error) {
        state.queued = false;
        showStatus(error.description || error.message || 'Could not update your cart. Please try again.', 5000);
      } finally {
        state.syncing = false;
        section.removeAttribute('aria-busy');
        if (state.queued) run();
      }
    };

    section.addEventListener('change', (event) => {
      if (!event.target.matches('[data-sv-setup-item], [data-sv-setup-skip]')) return;
      updateQuickAdd(section);
      if (event.target.matches('[data-sv-setup-board]')) return;
      state.queued = true;
      run();
    });

    section.addEventListener('click', (event) => {
      const button = event.target.closest('[data-sv-setup-toggle]');
      if (!button || !section.contains(button)) return;
      const input = section.querySelector(`#${CSS.escape(button.dataset.svSetupToggle)}`);
      if (!input || input.disabled) return;
      input.checked = !input.checked;
      input.dispatchEvent(new Event('change', {bubbles: true}));
    });

    section.querySelectorAll('.sv-setup-options, .sv-category-setup__options').forEach(initialiseScroller);
    orderByFamily(section);
    initialiseStickyCart(section);
    updateQuickAdd(section);
  };

  const initialiseAll = (root = document) => {
    if (root.matches && root.matches(selector)) initialise(root);
    root.querySelectorAll(selector).forEach(initialise);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
